import React from "react";
import {
  Alert,
  Box,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

import {
  DashboardAlert,
  DashboardParams,
  ExecutiveInsight,
  useGetExecutiveSummaryQuery,
} from "../../store/apis/commercialDashboardApi";
import KpiCard from "./KpiCard";
import BillingCharts from "./BillingCharts";
import { hatch, JourneyRow, StackBar } from "./DashboardCharts";
import { SheetTarget } from "./DashboardSheets";
import {
  BlockGrid,
  Empty,
  KpiGrid,
  QualityChip,
  Section,
} from "./DashboardBits";
import {
  days,
  euro,
  label,
  percent,
  PROPOSAL_STATUS_LABEL,
  stageLabel,
} from "./dashboardFormat";

const ALERT_LABEL: Record<string, string> = {
  ENTREGA_INTERNA_VENCIDA: "Entrega interna vencida",
  ENTREGADA_SIN_PROXIMO_PASO: "Entregada sin próximo paso",
  SEGUIMIENTO_VENCIDO: "Seguimiento vencido",
  SIN_PRESUPUESTO: "Sin presupuesto",
  PROXIMO_PASO_RELEVANTE: "Próximo paso relevante",
};

/** Las fases de cliente juntas en el recorrido: el detalle está en el tablero. */
const CLIENT_STAGES = new Set([
  "CLIENTE_ALWAYS_ON",
  "CLIENTE_EVENTUAL",
  "CLIENTE_ESTACIONAL",
  "CLIENTE_TEST",
]);

const AlertRow: React.FC<{ alert: DashboardAlert; onOpen: () => void }> = ({
  alert,
  onOpen,
}) => {
  const theme = useTheme();
  const color =
    alert.severity === "RED"
      ? theme.palette.error.main
      : alert.severity === "AMBER"
        ? theme.palette.warning.main
        : theme.palette.info.main;
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
      data-testid={`alert-${alert.type}-${alert.proposalId}`}
      sx={{
        borderLeft: `4px solid ${color}`,
        borderRadius: 1,
        px: 1.5,
        py: 1,
        cursor: "pointer",
        "&:hover": { backgroundColor: theme.palette.action.hover },
      }}
    >
      <Stack direction="row" justifyContent="space-between" gap={1}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {ALERT_LABEL[alert.type] ?? alert.type} · {alert.clientName}
        </Typography>
        <Typography variant="body2">
          {alert.amount === null ? "Sin indicar" : euro.format(alert.amount)}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {alert.proposalNumber} · {alert.message}
      </Typography>
    </Box>
  );
};

const InsightRow: React.FC<{ insight: ExecutiveInsight }> = ({ insight }) => {
  const severity =
    insight.severity === "RED"
      ? "error"
      : insight.severity === "AMBER"
        ? "warning"
        : insight.severity === "GREEN"
          ? "success"
          : "info";
  return (
    <Alert severity={severity} sx={{ py: 0 }}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {insight.message}
      </Typography>
      <Typography variant="caption">{insight.action}</Typography>
    </Alert>
  );
};

interface Props {
  params: DashboardParams;
  onOpen: (target: SheetTarget) => void;
}

/** 01 · Resumen ejecutivo: si llegamos al objetivo, qué negocio viene y qué decidir esta semana. */
const SummaryTab: React.FC<Props> = ({ params, onOpen }) => {
  const theme = useTheme();
  const { data, isLoading, isError } = useGetExecutiveSummaryQuery(params);
  const view = data?.data;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={6}>
        <CircularProgress />
      </Box>
    );
  }
  if (isError || !view) {
    return (
      <Alert severity="error">No se pudo cargar el resumen ejecutivo.</Alert>
    );
  }

  const { kpis, target } = view;
  const commercialJourney = [
    ...view.journey.commercial.filter((step) => !CLIENT_STAGES.has(step.stage)),
  ];
  const clientStep = view.journey.commercial
    .filter((step) => CLIENT_STAGES.has(step.stage))
    .reduce(
      (total, step) => ({
        stage: "CLIENTE",
        count: total.count + step.count,
        amount: total.amount + step.amount,
      }),
      { stage: "CLIENTE", count: 0, amount: 0 }
    );
  // Cliente va detrás de Negociación, antes de Pausado, Excliente y Rechazada.
  const negotiationIndex = commercialJourney.findIndex(
    (step) => step.stage === "NEGOCIACION"
  );
  commercialJourney.splice(negotiationIndex + 1, 0, clientStep);

  return (
    <Stack gap={2}>
      <KpiGrid columns={4}>
        <KpiCard
          label="Pipeline comercial · NB"
          value={euro.format(kpis.commercialPipeline.amount)}
          caption={`${kpis.commercialPipeline.count} empresas, de Lead a Negociación`}
          orientative
        />
        <KpiCard
          label="Negocio abierto (entregado)"
          value={euro.format(kpis.openDelivered.amount)}
          caption={`${kpis.openDelivered.count} propuestas · ponderado ${
            kpis.openDelivered.weightedAmount === null
              ? "— (sin probabilidades en Twenty)"
              : euro.format(kpis.openDelivered.weightedAmount)
          }`}
          orientative
        />
        <KpiCard
          label="Negocio ganado"
          value={euro.format(kpis.won.amount)}
          caption={
            target
              ? `${percent(target.progress)} del objetivo de ${euro.format(target.target)}`
              : view.targetUnavailableReason === "FILTROS"
                ? `${kpis.won.count} aceptadas · el objetivo es solo de empresa`
                : `${kpis.won.count} aceptadas · sin objetivo fijado`
          }
          orientative
          footer={
            target ? (
              <LinearProgress
                variant="determinate"
                value={Math.min(100, target.progress * 100)}
                sx={{ mt: 1, height: 6, borderRadius: 3 }}
                aria-label="Avance frente al objetivo"
              />
            ) : undefined
          }
        />
        <KpiCard
          label="Tasa de cierre"
          value={percent(kpis.closeRate.byCount)}
          caption={`Por valor: ${percent(kpis.closeRate.byValue)}`}
        />
      </KpiGrid>
      <KpiGrid columns={3}>
        <KpiCard
          label="Entrada → aceptación"
          value={days(kpis.entryToAcceptance.mean)}
          caption={`Mediana ${days(kpis.entryToAcceptance.median)} · el cliente tarda ${days(
            kpis.deliveryToResponse.mean
          )} en responder`}
        />
        <KpiCard
          label="Entrada → envío de la propuesta"
          value={days(kpis.entryToDelivery.mean)}
          caption={`Mediana ${days(kpis.entryToDelivery.median)} · ${percent(
            kpis.onTimeRate
          )} entregadas en fecha`}
        />
        <KpiCard
          label="Horas por propuesta"
          value="—"
          caption="Llegará con las horas de Toggl por número de propuesta"
        />
      </KpiGrid>

      <BlockGrid>
        <Section
          title="Lectura ejecutiva"
          subtitle="Generada a partir de los datos, ordenada por impacto"
        >
          {view.insights.length === 0 ? (
            <Empty>Nada que destacar con los filtros actuales.</Empty>
          ) : (
            <Stack gap={1}>
              {view.insights.map((insight) => (
                <InsightRow key={insight.key} insight={insight} />
              ))}
            </Stack>
          )}
        </Section>

        <Section
          title="Requiere decisión esta semana"
          subtitle="Pulsa un aviso para abrir la ficha"
        >
          {view.alerts.length === 0 ? (
            <Empty>No hay avisos.</Empty>
          ) : (
            <Stack gap={0.75}>
              {view.alerts.map((alert) => (
                <AlertRow
                  key={`${alert.type}-${alert.proposalId}`}
                  alert={alert}
                  onOpen={() =>
                    onOpen({
                      type: "proposal",
                      id: alert.proposalId,
                      note: `${ALERT_LABEL[alert.type] ?? alert.type}: ${alert.message}`,
                    })
                  }
                />
              ))}
            </Stack>
          )}
        </Section>
      </BlockGrid>

      <Section
        title="Recorrido del negocio"
        subtitle="Del primer contacto al cliente, y de la solicitud a la decisión"
        action={<QualityChip />}
      >
        <JourneyRow
          title="Pipeline comercial"
          subtitle="New Business · potencial orientativo"
          chips={commercialJourney.map((step) => ({
            label:
              step.stage === "CLIENTE" ? "Cliente" : stageLabel(step.stage),
            count: step.count,
            value: euro.format(step.amount),
            tone:
              step.stage === "CLIENTE"
                ? "win"
                : step.stage === "RECHAZADA"
                  ? "lose"
                  : step.stage === "FUEGO_LENTO"
                    ? "slow"
                    : undefined,
          }))}
        />
        <JourneyRow
          title="Pipeline de propuestas"
          subtitle="NB + EB · aceptadas y rechazadas del periodo"
          chips={view.journey.proposals.map((step) => ({
            label: label(PROPOSAL_STATUS_LABEL, step.status),
            count: step.count,
            value: euro.format(step.amount),
            tone:
              step.status === "ACEPTADA"
                ? "win"
                : step.status === "RECHAZADA"
                  ? "lose"
                  : undefined,
          }))}
        />

        <Box mt={2}>
          <Typography variant="subtitle2" mb={1}>
            Camino al objetivo anual
            {target ? ` · ${euro.format(target.target)}` : ""}
          </Typography>
          {target ? (
            <>
              <StackBar
                total={Math.max(target.target, target.won + target.weighted)}
                segments={[
                  {
                    label: "Ganado",
                    value: target.won,
                    background: theme.palette.primary.main,
                  },
                  {
                    label: "Entregado ponderado",
                    value: target.weighted,
                    background: theme.palette.primary.light,
                  },
                  {
                    label: "Hueco sin cubrir",
                    value: target.gap,
                    background: hatch(theme.palette.warning.main),
                    dashed: true,
                  },
                ]}
              />
              {target.newProposalsNeeded !== null && target.gap > 0 && (
                <Typography variant="body2" mt={1}>
                  Para cubrir el hueco hacen falta unos{" "}
                  <b>{euro.format(target.newProposalsNeeded)}</b> en propuestas
                  nuevas (hueco ÷ tasa de cierre por valor).
                </Typography>
              )}
            </>
          ) : (
            <Empty>
              {view.targetUnavailableReason === "FILTROS"
                ? "El objetivo es solo de empresa: quita los filtros de owner, House, tipo de cliente y negocio para verlo."
                : "No hay objetivo fijado para este año. Dirección puede fijarlo en Parámetros."}
            </Empty>
          )}
        </Box>
      </Section>

      <BillingCharts billing={view.billing} onOpen={onOpen} />
    </Stack>
  );
};

export default SummaryTab;
